import { useState, useRef, useEffect } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import axios from 'axios';

import { API } from '../utils/apiConfig';
import { speakPointSet } from '../utils/tts';

export const AddressAutocomplete = ({ value, onChange, onSelect, placeholder, label, required, testId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await axios.get(`${API}/geo/autocomplete`, { params: { q: val, limit: 5 } });
        setSuggestions(resp.data.suggestions || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const handleSelect = (suggestion) => {
    const displayValue = suggestion.poi_name || suggestion.short_name || suggestion.city || suggestion.display_name.split(',')[0];
    onChange(displayValue);
    onSelect(suggestion);
    setSuggestions([]);
    setOpen(false);

    // Voice feedback: "Punkt gesetzt: [address]"
    speakPointSet(displayValue);

    // Brief highlight animation on the input field
    setHighlighted(true);
    setTimeout(() => setHighlighted(false), 1200);
  };

  return (
    <div className="form-group" ref={wrapperRef} style={{ position: 'relative' }}>
      {label && <label>{label} {required && '*'}</label>}
      <div className="input-wrapper" style={{
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
        boxShadow: highlighted ? '0 0 0 3px rgba(37, 99, 235, 0.3)' : 'none',
        borderColor: highlighted ? '#2563eb' : undefined,
        borderRadius: '8px',
      }}>
        <MapPin size={18} style={{ color: highlighted ? '#2563eb' : undefined, transition: 'color 0.3s' }} />
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          data-testid={testId}
        />
        {loading && <Loader2 size={16} className="spinner" style={{ position: 'absolute', right: '12px', animation: 'spin 1s linear infinite' }} />}
      </div>

      {open && suggestions.length > 0 && (
        <ul
          className="autocomplete-dropdown"
          data-testid={`${testId}-dropdown`}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 50,
            background: 'var(--bg-card, #fff)',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: '0 0 8px 8px',
            maxHeight: '220px',
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {suggestions.map((s, i) => (
            <li
              key={i}
              onClick={() => handleSelect(s)}
              data-testid={`${testId}-option-${i}`}
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                borderBottom: '1px solid var(--border-color, #f0f0f0)',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover, #f7f7f7)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <MapPin size={14} style={{ flexShrink: 0, opacity: 0.5 }} />
              <div>
                <div style={{ fontWeight: 500 }}>{s.poi_name || s.short_name || s.city || s.display_name.split(',')[0]}</div>
                {(s.poi_name ? s.city : s.state) && (
                  <div style={{ fontSize: '0.78rem', opacity: 0.6 }}>
                    {s.poi_name ? (s.city ? `${s.city}${s.state ? `, ${s.state}` : ''}` : s.state) : s.state}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
